import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { FormlyDropdownValue } from '@intellio/shared/models';
import { AnalyticsService } from '@intellio/shared/services';
import { NgSelectModule } from '@ng-select/ng-select';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { isObservable, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { FormlyComponentTelemetry } from './formly-component-telemetry';

@Component({
  selector: 'itc-formly-field-typeahead',
  template: `
    @if (this.staticOptions) {
      <span>
        <input
          type="text"
          matInput
          [readonly]="props.readonly"
          [required]="props.required"
          [formControl]="control"
          [placeholder]="props.placeholder"
          [matAutocomplete]="auto"
          />
          <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn">
            @for (option of filteredOptions | async; track option) {
              <mat-option
                [value]="option"
                [disabled]="option.disabled"
                >
                {{ option?.label }}
              </mat-option>
            }
          </mat-autocomplete>
        </span>
      } @else {
        @if (props.hasLocations !== undefined && props.hasLocations !== null) {
          <span
            >
            @if (!props.hasLocations) {
              <strong>
                You have no existing applications. Please create a new one by
                selecting 'A new system' above.
              </strong>
            }
            <input
              type="text"
              matInput
              [formControl]="control"
              [matAutocomplete]="autoComp"
              [required]="props.required"
              />
              <mat-autocomplete #autoComp="matAutocomplete" [displayWith]="displayFn">
                @for (option of ((props.search$ | async) ?? []); track option) {
                  <div>
                    <mat-option
                      [value]="option"
                      [disabled]="option.disabled"
                    >
                      {{ option?.label }}
                    </mat-option>
                  </div>
                }
              </mat-autocomplete>
            </span>
          }
          @if (props.hasLocations === undefined || props.hasLocations === null) {
            <span
              >
              <ng-select
                [id]="id"
                [clearable]="true"
                [multiple]="false"
                placeholder="{{ props.placeholder + (props.required ? ' *' : '') }}"
                [formControl]="control"
                [required]="props.required"
                (search)="onSearch($event)"
                >
                <cdk-virtual-scroll-viewport [itemSize]="42">
                  @for (option of results; track option) {
                    <ng-option
                      [value]="option.value"
                      [disabled]="option.disabled"
                    >
                      {{ option.label }}
                    </ng-option>
                  }
                </cdk-virtual-scroll-viewport>
              </ng-select>
            </span>
          }
        }
    `,
  imports: [
    NgSelectModule,
    CdkVirtualScrollViewport,
    ReactiveFormsModule,
    MatOptionModule,
    MatAutocompleteModule,
    CommonModule,
    MatInputModule,
    AsyncPipe,
    NgSelectModule,
    ScrollingModule,
  ],
})
export class TypeAheadTypeComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  constructor(
    protected cd: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {
    super();
  }

  control: UntypedFormControl;
  onDestroy$ = new Subject<void>();
  results: FormlyDropdownValue[];
  jsonOptions: any[] | Observable<any[]>;
  reducedOptions: any;
  filteredOptions: Observable<any[]>;
  staticOptions: boolean = false;
  private telemetry = new FormlyComponentTelemetry(
    (eventName, properties) => this.analyticsService.logEvent(eventName, properties),
    () => ({ fieldKey: this.field?.key?.toString() })
  );

  displayFn = (val) => val?.label;

  ngOnInit() {
    this.telemetry.mark('init');
    this.control = this?.formControl as UntypedFormControl;
    this.jsonOptions = this?.props.options as any[];
    if (this.props['staticOptions'] === true) {
      this.staticOptions = true;
      // when expressions run to update the options, use the updated value
      this.field.options.fieldChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((e) => {
        if (
          e.type === 'expressionChanges' &&
          e.field.key === this.field.key &&
          e.property === 'props.options'
        ) {
          if (this.telemetry.shouldEmit('options-expression')) {
            this.telemetry.event('FormlyPerf.TypeaheadOptionsExpressionChange');
          }
          this.jsonOptions = e.value;
          this.control.patchValue(''); // triggers the valueChanges below which populates filteredOptions
        }
      });
      // filter options based on search
      this.filteredOptions = this.control.valueChanges.pipe(
        startWith(''),
        switchMap((search) => {
          if (typeof search !== 'string') {
            return of([]);
          }

          let lowerSearch = search.toLowerCase();
          const options$ = isObservable(this.jsonOptions)
            ? this.jsonOptions
            : of(this.jsonOptions ?? []);

          return options$.pipe(
            map((options) => {
              const filtered = (options ?? []).filter(
              (option) =>
                option.label.toLowerCase().includes(lowerSearch) ||
                option.value.toLowerCase().includes(lowerSearch)
              );
              if (
                this.telemetry.shouldEmit('filter-computed') &&
                lowerSearch.length >= 2
              ) {
                this.telemetry.event('FormlyPerf.TypeaheadFilterComputed', {
                  searchLength: lowerSearch.length,
                  optionCount: filtered.length,
                });
              }
              return filtered;
            })
          );
        })
      );
    } else {
      if (
        this.props.hasLocations === undefined ||
        this.props.hasLocations === null
      ) {
        (this.props.options as Observable<FormlyDropdownValue[]>)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((options) => {
            this.results = [...(options ?? [])];
            // ng-select can leave the open panel showing "No items found" until
            // the next user interaction unless projected options are checked.
            this.cd.detectChanges();
            const pendingSearchMs = this.telemetry.elapsedMs('search-start');
            if (pendingSearchMs !== null) {
              this.telemetry.event('FormlyPerf.TypeaheadSearchResultsLatency', {
                durationMs: pendingSearchMs,
                optionCount: options?.length ?? 0,
              });
              this.telemetry.clear('search-start');
            } else {
              this.telemetry.event('FormlyPerf.TypeaheadInitialOptionsLoaded', {
                durationMs: this.telemetry.elapsedMs('init') ?? 0,
                optionCount: options?.length ?? 0,
              });
            }
          });
      }
    }
  }

  onSearch(searchTerms: {term: string, items: any[]}) {
    this.telemetry.mark('search-start');

    this.field?.options?.fieldChanges?.next({
      type: 'typeaheadInputChanges',
      field: this.field,
      value:searchTerms.term,
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
